import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import TextType from '../../components/TextType';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide2.css';

/* ─── SVG Background Layers ─── */
function BlueprintGrid() {
  return (
    <svg className="s2-blueprint-grid" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="smallGrid" width="30" height="30" patternUnits="userSpaceOnUse">
          <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
        </pattern>
        <pattern id="largeGrid" width="150" height="150" patternUnits="userSpaceOnUse">
          <rect width="150" height="150" fill="url(#smallGrid)" />
          <path d="M 150 0 L 0 0 0 150" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.8" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#largeGrid)" />
    </svg>
  );
}

function CompassRadar({ reduceMotion }) {
  return (
    <svg className="s2-compass" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
      {/* Radar rings */}
      {[80, 130, 180, 230].map((r, i) => (
        <circle key={i} cx="250" cy="250" r={r} fill="none"
          stroke="rgba(255,255,255,0.08)" strokeWidth="0.6"
          strokeDasharray={i % 2 === 0 ? 'none' : '4 6'} />
      ))}
      {/* Cross lines */}
      <line x1="250" y1="30" x2="250" y2="470" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
      <line x1="30" y1="250" x2="470" y2="250" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
      {/* Diagonal lines */}
      <line x1="95" y1="95" x2="405" y2="405" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      <line x1="405" y1="95" x2="95" y2="405" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      {/* Tick marks around outer ring */}
      {Array.from({ length: 36 }, (_, i) => {
        const angle = (i * 10 * Math.PI) / 180;
        const x1 = 250 + 225 * Math.cos(angle);
        const y1 = 250 + 225 * Math.sin(angle);
        const x2 = 250 + 235 * Math.cos(angle);
        const y2 = 250 + 235 * Math.sin(angle);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="rgba(255,255,255,0.10)" strokeWidth="0.8" />;
      })}
      {/* Sweep line */}
      {!reduceMotion && (
        <line x1="250" y1="250" x2="250" y2="30" stroke="rgba(255,255,255,0.12)" strokeWidth="1">
          <animateTransform attributeName="transform" type="rotate"
            from="0 250 250" to="360 250 250" dur="20s" repeatCount="indefinite" />
        </line>
      )}
      {/* Center dot */}
      <circle cx="250" cy="250" r="3" fill="rgba(255,255,255,0.14)" />
    </svg>
  );
}

function WaveLines({ reduceMotion }) {
  return (
    <svg className="s2-waves" viewBox="0 0 1440 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      {[
        { d: 'M0,120 C360,60 720,180 1080,100 C1260,60 1380,130 1440,100', opacity: 0.10, delay: '0s' },
        { d: 'M0,140 C240,90 480,170 720,120 C960,70 1200,160 1440,120', opacity: 0.07, delay: '-3s' },
        { d: 'M0,160 C300,110 600,190 900,140 C1100,100 1300,170 1440,140', opacity: 0.05, delay: '-6s' },
      ].map((wave, i) => (
        <g key={i}>
          <path d={wave.d} fill="none" stroke={`rgba(255,255,255,${wave.opacity})`}
            strokeWidth="1.2" className={!reduceMotion ? `s2-wave-path s2-wave-path-${i + 1}` : ''} />
        </g>
      ))}
    </svg>
  );
}

/* ─── Signal Node (value marker) ─── */
function SignalNode() {
  return (
    <span className="s2-signal-node">
      <span className="s2-signal-core"></span>
      <span className="s2-signal-ring"></span>
    </span>
  );
}

/* ─── Main Slide ─── */
function Slide2() {
  const prefersReduced = useReducedMotion();
  const reduceMotion = !!prefersReduced;

  const [isVisible, setIsVisible] = useState(false);
  const [aboutIndex, setAboutIndex] = useState(0);
  useEffect(() => { setIsVisible(true); }, []);

  const aboutTexts = [
    `Avatec Marine is an international company providing newbuilding and retrofit solutions for marine automation systems, navigation electronics, radio communications, satellite connectivity, fire & gas detection, liquid level gauging, ultrasonic antifouling systems, and safety appliances. Built on quality, experience, and reliability, Avatec Marine delivers full control over safety performance and measurable results for reduced downtime.`,
    `Established in 2015 with global reach from service hubs in the Netherlands, Germany, Finland, Sweden, Malta, Turkey, Azerbaijan, Morocco, India, and the Baltic States (Estonia, Latvia, Lithuania), Avatec Marine unites more than 100 employees across 12 countries. Cutting-edge technologies and considerable experience in marine electronics enable us to provide high-quality services and products.`,
  ];

  const cycleAbout = useCallback(() => {
    setAboutIndex((prev) => (prev + 1) % aboutTexts.length);
  }, [aboutTexts.length]);

  useEffect(() => {
    const interval = setInterval(cycleAbout, 6000);
    return () => clearInterval(interval);
  }, [cycleAbout]);

  const fadeUp = (delay = 0) =>
    reduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 30 },
          animate: isVisible ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        };

  const values = [
    { keyword: 'Ownership', desc: 'We expect excellence by taking responsibility for our actions.' },
    { keyword: 'Strive', desc: 'We deliver results that make a difference and exceed expectations.' },
    { keyword: 'Collaborate', desc: 'We see collective success as our own through team effort.' },
  ];

  return (
    <div className="slide slide2">
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />
      {/* Background layers */}
      <div className="s2-bg-layers">
        <BlueprintGrid />
        <CompassRadar reduceMotion={reduceMotion} />
        <WaveLines reduceMotion={reduceMotion} />
        <div className="s2-noise"></div>
      </div>

      {/* Section number */}
      <motion.span className="s2-section-number"
        {...(reduceMotion
          ? { initial: { opacity: 0.05 }, animate: { opacity: 0.05 } }
          : { initial: { opacity: 0, x: 40 }, animate: isVisible ? { opacity: 0.05, x: 0 } : {}, transition: { duration: 1.2, delay: 0.1 } }
        )}>
        02
      </motion.span>

      {/* Main content */}
      <div className="s2-container">
        <div className="s2-grid">

          {/* LEFT — About Us */}
          <motion.div className="s2-card s2-card-about" {...fadeUp(0.2)}>
            <span className="s2-label">ABOUT US</span>
            <div className="s2-about-text-wrapper">
              <AnimatePresence mode="wait">
                <motion.p
                  key={aboutIndex}
                  className="s2-card-text"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  {aboutTexts[aboutIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
            <div className="s2-about-tagline">
              <TextType
                text={['Solutions to Power', 'Your Business', 'Globally']}
                typingSpeed={60}
                deletingSpeed={40}
                pauseDuration={2500}
                loop={true}
                showCursor={true}
                cursorCharacter="|"
                className="s2-typing-text"
              />
            </div>
            <div className="s2-about-dots">
              {aboutTexts.map((_, i) => (
                <button
                  key={i}
                  className={`s2-about-dot ${i === aboutIndex ? 's2-about-dot-active' : ''}`}
                  onClick={() => setAboutIndex(i)}
                  aria-label={`Text ${i + 1}`}
                />
              ))}
            </div>
          </motion.div>

          {/* RIGHT TOP — Our Mission */}
          <motion.div className="s2-card s2-card-mission" {...fadeUp(0.45)}>
            <span className="s2-label">OUR MISSION</span>
            <ul className="s2-mission-list">
              <li>To support our clients with high-quality services and reliable products for safe operations.</li>
              <li>To be a modern company for our employees to develop and apply their professional skills.</li>
              <li>To create an ecosystem for a smarter, safer, and more sustainable future in the shipping industry.</li>
            </ul>
          </motion.div>

          {/* RIGHT BOTTOM — Our Values */}
          <motion.div className="s2-card s2-card-values" {...fadeUp(0.65)}>
            <span className="s2-label">OUR VALUES</span>
            <ul className="s2-values-list">
              {values.map((v, i) => (
                <li key={i} className="s2-value-item">
                  <SignalNode />
                  <span><strong className="s2-value-keyword">{v.keyword}</strong> — {v.desc}</span>
                </li>
              ))}
            </ul>
          </motion.div>

        </div>
      </div>
    </div>
  );
}

export default Slide2;
